import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { saveAs } from 'file-saver-es'
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import ExcelJS from "exceljs";
import { MenuItem, PrimeIcons } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { createCompareContextMenuItems, isDepthColumn, openCompareDialog, shouldSkipCompareForTable } from '../ng-table-grid/compare-dialog.helpers';

@Component({
  selector: 'app-ng-table-results',
  templateUrl: './ng-table-results.component.html',
  styles: [
    `

      .redFlag {
        font-weight: 400;
        color: #FF5252;
      }

      .orangeFlag {
        font-weight: 400;
        color: orange;
      }
    `
  ],
  providers: [DialogService],
  standalone: false
})
export class NgTableResultsComponent implements OnInit, OnDestroy {

  private _subscriptions: Subscription = new Subscription();
  private _selection: { cellText: string, cellKeys: { row: number; column: number } }[];

  public menuItems: MenuItem[];
  selectedColumns: string[] = [];
  public compareContextMenuItems: MenuItem[] = [];

  @ViewChild('compareMenu') compareContextMenu: ContextMenu;

  @Input()
  public minTemp: number;

  @Input()
  public maxTemp: number;

  @Input()
  public showHeatmap: boolean;

  @Input()
  public tableData: any[];

  @Input()
  public apiCollapseType: string;

  @Input()
  public hangerLiftoffForce: number;

  @Input()
  public shearPinRating: number;

  @Input()
  public upwardMovementLimit: number;

  @Input()
  public downwardMovementLimit: number;

  @Input()
  public hollidayStressRatio: number;

  @Input()
  public tableName: string;

  @Input()
  public tableHeight: string = '600px';

  @Input()
  public selectedOperation: string;

  @Input()
  public virtualScroll: string;

  @Input()
  public mode: string;

  @Input()
  public columnDefinitions: { header: string, field: string, valueFormatter(params: any): string, color: string }[];

  @Input()
  public autoHeight: boolean = false;

  @Input()
  public lengthUnit: string = 'ft';

  @HostListener('document:mousedown', ['$event'])
  onClick(event: MouseEvent): void {
    this._isActive = this.elementRef.nativeElement.contains(event.target);
    if (!this._isActive) {
      this.clearColumnData();
    }
  }

  private _isActive = false;

  private columnSelected: boolean;
  private columnsForCopy: any = [];
  private columnsForCopyBackup: any = [];
  private isCopied = false;
  private includeHeaders = true;
  private isCopyTable: boolean;

  private _tableHeightSignal = signal<string>('600px');
  private _resizeObserver: ResizeObserver | null = null;
  private _lastTableHeight = '600px';
  private _resizeDebounceTimer: any;
  private _observedElement: HTMLElement | null = null;

  get computedTableHeight(): string {
    return this.autoHeight ? this._tableHeightSignal() : this.tableHeight;
  }

  private _initAutoHeight(): void {
    // Observe the fieldset (GridStack content area) or gridstack-item (full cell) for the container height.
    // Then dynamically calculate the offset by measuring how far down the table is positioned.
    const hostElement = this.elementRef.nativeElement as HTMLElement;
    this._observedElement = (
      hostElement.closest('fieldset') ??
      hostElement.closest('.grid-stack-item') ??
      hostElement.closest('gridstack-item') ??
      hostElement.parentElement
    ) as HTMLElement | null ?? hostElement;

    this._resizeObserver = new ResizeObserver(entries => {
      const containerHeight = entries[0]?.contentRect.height;
      if (!containerHeight || containerHeight < 120) return;

      // Calculate dynamic offset: measure the table's top position relative to the observed container.
      // This accounts for workspace headers, toolbars, and any other UI above the table.
      const containerRect = this._observedElement!.getBoundingClientRect();
      const tableRect = hostElement.getBoundingClientRect();
      const offsetFromTop = Math.max(0, Math.round(tableRect.top - containerRect.top));
      
      // Add small buffer for borders/padding, and ensure we leave room for at least one row.
      const minOffset = offsetFromTop + 10;
      const computedHeight = containerHeight - minOffset;
      
      if (computedHeight < 120) return;

      const val = computedHeight + 'px';
      const lastNum = parseFloat(this._lastTableHeight);
      const diff = Math.abs(computedHeight - lastNum);
      
      if (diff > 15) {
        clearTimeout(this._resizeDebounceTimer);
        this._lastTableHeight = val;
        this._tableHeightSignal.set(val);
      } else {
        clearTimeout(this._resizeDebounceTimer);
        this._resizeDebounceTimer = setTimeout(() => {
          this._lastTableHeight = val;
          this._tableHeightSignal.set(val);
        }, 200);
      }
    });
    this._resizeObserver.observe(this._observedElement);
  }

  constructor(private elementRef: ElementRef, private _dialogService: DialogService) {
    this.menuItems = [
      { label: 'Excel', icon: PrimeIcons.FILE_EXCEL, command: () => { this.exportExcel() } },
      { label: 'PDF', icon: PrimeIcons.FILE_PDF, command: () => { this.exportPdf() } },
      { label: 'CSV', icon: PrimeIcons.FILE, command: () => { this.exportCsv() } }
    ];
  }

  ngOnInit(): void {
    if (this.autoHeight) {
      this._initAutoHeight();
    }

    // Overwrite what is being copied to the clipboard.
    window.addEventListener('copy', this._onCopy);
  }

  private _onCopy = (e: ClipboardEvent): void => {
    if (!this._isActive) {
      return;
    }

    if (this.columnSelected) {
      if (this.includeHeaders) {
        this.copy(e);
      } else {
        if (!this.isCopyTable) {
          this.removeHeaders();
        }
        this.copy(e);
      }
      this.isCopyTable = false;
    } else if (this._selection?.length) {
      const csv = this.createTabDelimitedForClipboard();
      e.clipboardData.setData('text', csv);
      e.preventDefault();
      this.isCopied = true;
    }
  };

  private static readonly HEATMAP_GRADIENT: string[] = [
    "#42A5F5", "#64B5F6", "#90CAF9", "#BBDEFB",
    "#FFEB3B", "#FDD835", "#FBC02D", "#F9A825",
    "#F57F17", "#EF6C00", "#E65100", "#D84315",
    "#BF360C", "#A82710"
  ];

  public getColorForTemperature(value: number, field: string): string {
    if (!this.showHeatmap || field.startsWith('md') || field.startsWith('tvd')) {
      return '';
    }

    const range = this.maxTemp - this.minTemp;
    if (range <= 0) {
      return NgTableResultsComponent.HEATMAP_GRADIENT[0];
    }

    const t = Math.max(0, Math.min(1, (value - this.minTemp) / range));
    const lastIndex = NgTableResultsComponent.HEATMAP_GRADIENT.length - 1;
    const index = Math.min(Math.floor(t * lastIndex), lastIndex);
    return NgTableResultsComponent.HEATMAP_GRADIENT[index];
  }

  private copy(e: any) {
    e.clipboardData.setData('text', this.columnsForCopy);
    e.preventDefault();
    this.isCopied = true;
  }

  headers(e: Event) {
    this.includeHeaders = e['checked'];
  }

  onRangeChanged(range: { cellText: string, cellKeys: { row: number; column: number } }[]) {
    this.columnSelected = false;
    this._selection = range;
  }

  copyTable(columns) {
    // Foreach item in columns call selectColumn
    columns.forEach(col => {
      this.selectColumn(col, columns);
    });

    this.isCopyTable = true;
    if (!this.includeHeaders) {
      this.removeHeaders();
    }
    document.execCommand('copy');
  }

  removeHeaders() {
    const newCsv: any = [];

    const lines = this.columnsForCopy.split("\n");
    lines.forEach(function (item, i) {
      if (i !== 0) newCsv.push(item);
    })

    this.columnsForCopy = newCsv.join("\n");
  }

  selectColumn(col, columns) {
    this.columnSelected = true;
    if (this.columnsForCopyBackup.findIndex(x => x == col.header) >= 0) {
      return;
    }

    if (this.isCopied) { 
      this.clearColumnData();
    }

    if (this.columnsForCopyBackup.length > 1) {
      const headerExists = this.columnsForCopyBackup[0].findIndex(x => x == col.header);
      if (headerExists >= 0) {
        for (const columnArray of this.columnsForCopyBackup) {
          if (headerExists !== -1) {
            columnArray.splice(headerExists, 1);
          }
        }
        this.columnsForCopy = this.columnsForCopyBackup.map(v => v.map(x => `${x}`).join("\t")).join('\n');
        return;
      }
    }

    const column = this.getColumnData(col);
    if (this.columnsForCopyBackup.length > 0) {
      this.columnsForCopyBackup[0].push(col.header);
      for (let l = 1; l < this.columnsForCopyBackup.length; l++) {
        this.columnsForCopyBackup[l].push(column[l - 1][0]);
      };
    } else {
      this.columnsForCopyBackup = this.getColumnData(col);
      this.columnsForCopyBackup.unshift([col.header]);
    }

    const headers = columns.map(x => x.header);

    if (this.columnsForCopyBackup[0].length > 1) {
      const headerIndex = headers.findIndex(x => x == this.columnsForCopyBackup[0][this.columnsForCopyBackup[0].length - 1]);
      const previousSelectedHeaderIndex = headers.findIndex(x => x == this.columnsForCopyBackup[0][this.columnsForCopyBackup[0].length - 2]);
      if (headerIndex < previousSelectedHeaderIndex) {
        const existingHeaders = headers.filter(ar => this.columnsForCopyBackup[0].find(rm => (rm === ar)));
        for (const columnArray of this.columnsForCopyBackup) {
          if (columnArray.length > 0) {
            const element = columnArray[columnArray.length - 1];
            columnArray.splice(columnArray.length - 1, 1);
        
            const index = existingHeaders.findIndex(x => x === col.header);
            if (index !== -1) {
              columnArray.splice(index, 0, element);
            }
          }
        }
      }
    }

    this.columnsForCopy = this.columnsForCopyBackup.map(v => v.map(x => `${x}`).join("\t")).join('\n');
  }

  private getColumnData(col : any) {
    return this.tableData.map(x => [x[col.field]]);
  }

  toggleColumnHighlight(event: MouseEvent, col: any): void {
    const isCtrlPressed = event.ctrlKey || event.metaKey;
    if (isCtrlPressed) {
      const index = this.selectedColumns.indexOf(col.field);
      if (index === -1) {
        this.selectedColumns.push(col.field);
      } else {
        this.selectedColumns.splice(index, 1);
      }
    } else {
      this.selectedColumns = [];
      this.columnsForCopy = [];
      this.columnsForCopyBackup = [];
      this.selectedColumns = [col.field];
    }

    this.selectColumn(col, this.selectedColumns);
  }

  clearColumnData() {
    this.selectedColumns = [];
    this.columnsForCopy = [];
    this.columnsForCopyBackup = [];
    this.isCopied = false;
  }

  public exportPdf() {
    const doc = new jsPDF("l")
    const headerColumns = [this.columnDefinitions.map(col => col.header)];
    const data = this.tableData.map(d => {
      return Object.entries(d).map((e, i) => this.columnDefinitions[i].valueFormatter(e[1]))
    });
    autoTable(doc, { head: headerColumns, body: data });
    doc.save(`${this.tableName}.pdf`);
  }

  exportCsv = () => {
    const delimiter = ", ";
    const headers = this.columnDefinitions.map(x => `${x.header}`).join(delimiter);
    const outputData = this.tableData.map(x => Object.values(x));
    const csv = outputData.map(v => v.map(x => `${x}`).join(delimiter)).join('\n');;
    saveAs(new Blob([headers + '\n' + csv], { type: "application/csv;charset=utf-8" }), `${this.tableName}.csv`);
  }

  public async exportExcel(): Promise<void> {
    const mappedData = this.tableData.map(td => {
      const objWithHeaderKeys = {};
      this.columnDefinitions.forEach(cd => {
        const objHeaderKey = cd.header;
        objWithHeaderKeys[objHeaderKey.replace(/["']/g, '"')] = td[cd.field];
      });
      return objWithHeaderKeys;
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(this.tableName);

    worksheet.columns = Object.keys(mappedData[0]).map((key) => ({
      header: key,
      key: key,
      width: 45
    }));

    worksheet.addRows(mappedData);

    const buffer = await workbook.xlsx.writeBuffer();
    this.saveAsExcelFile(buffer, this.tableName);
  }

  public saveAsExcelFile(buffer: any, fileName: string): void {
    const selectedOperationName = this.selectedOperation ? this.selectedOperation['name'] : '';
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    saveAs(data, fileName + '_export_' + selectedOperationName + EXCEL_EXTENSION);
  }

  private createTabDelimitedForClipboard(): string {
    const selection = new Array(...this._selection);

    const rowMin = Math.min(...this._selection.map(f => f.cellKeys.row));
    const colMin = Math.min(...this._selection.map(f => f.cellKeys.column)); 
    const rowMax = Math.max(...this._selection.map(f => f.cellKeys.row));
    const colMax = Math.max(...this._selection.map(f => f.cellKeys.column));

    const grid = new Array<any[]>();

    for (let i = rowMin; i <= rowMax; i++) {
      const row = []
      for (let j = colMin; j <= colMax; j++) {
        const cell = selection.find(c => c.cellKeys.row === i && c.cellKeys.column === j);
        row.push(cell.cellText)
      }
      grid.push(row);
    }

    const csv = grid.map(v => v.map(x => `${x}`).join("\t")).join('\n');

    return csv
  }

  onColumnRightClick(event: MouseEvent, col: any): void {
    event.preventDefault();
    if (shouldSkipCompareForTable(this.tableName, [
      'Lift-Off Results',
      'Annular Pressure Build-Up Results',
      'Packer Results Table',
      'Load P&T Profiles',
      'Post-Install Load Cases'
    ])) {
      return;
    }

    if (isDepthColumn(col.field, this.columnDefinitions)) {
      return;
    }

    this.selectedColumns = [col.field];
    this.compareContextMenuItems = createCompareContextMenuItems(() =>
      openCompareDialog(this._dialogService, col, this.columnDefinitions, this.tableData)
    );

    this.compareContextMenu.show(event);
  }

  ngOnDestroy() {
    window.removeEventListener('copy', this._onCopy);
    clearTimeout(this._resizeDebounceTimer);
    this._resizeObserver?.disconnect();
    this._subscriptions?.unsubscribe();
  }
}
